import { useEffect, useRef, useState } from "react";

const TMDB_IMAGE_BASE_URL = "https://image.tmdb.org/t/p/w500";

// DomeGallery's default segments (35) * 5 rows = 175 tile slots. Fetching at
// least this many distinct movies up front means a user can spin the dome
// immediately without ever seeing a repeated poster. TMDB returns ~20
// movies per page, so ceil(175 / 20) = 9 pages minimum; a couple extra for
// margin plus id de-duplication overlap between lists.
const BURST_PAGES_PER_LIST = 6; // 6 * 2 lists * ~20 = ~240 movies up front
// After the burst, keep growing more slowly in the background so the pool
// keeps offering fresh variety without hammering the proxy.
const MAX_PAGES_PER_LIST = 5; // additional pages fetched slowly after burst
const FETCH_INTERVAL_MS = 4000;

function getFallbackPoster(title) {
  return `https://placehold.co/500x750/667eea/ffffff?text=${encodeURIComponent(
    title || "No Poster",
  )}`;
}

// One-line description from TMDB's overview field.
function toOneLineDescription(overview) {
  if (!overview) return "No description available.";
  const singleLine = overview.replace(/\s+/g, " ").trim();
  const MAX_LEN = 140;
  if (singleLine.length <= MAX_LEN) return singleLine;
  return singleLine.slice(0, MAX_LEN).replace(/\s+\S*$/, "") + "…";
}

function normalizeMovie(movie) {
  return {
    id: movie.id,
    src: movie.poster_path
      ? `${TMDB_IMAGE_BASE_URL}${movie.poster_path}`
      : getFallbackPoster(movie.title),
    alt: movie.title || "Movie poster",
    title: movie.title || "Untitled",
    description: toOneLineDescription(movie.overview),
  };
}

/**
 * Progressively builds a large, varied movie pool (popular + top_rated,
 * intentionally distinct from the Trending section's data source) for the
 * Explore dome gallery. Starts with a small pool for a fast first paint,
 * then fetches more pages in the background so the set of movies available
 * to the gallery keeps growing while the user is exploring.
 */
export default function useExploreMoviePool() {
  const [movies, setMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const seenIdsRef = useRef(new Set());
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const fetchPage = async (type, page) => {
      try {
        const res = await fetch(
          `/api/tmdb/discover?type=${encodeURIComponent(
            type,
          )}&page=${encodeURIComponent(page)}`,
        );
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data.results) ? data.results : [];
      } catch {
        return [];
      }
    };

    const mergeNewMovies = (rawMovies) => {
      const fresh = [];
      for (const m of rawMovies) {
        if (!m?.id || seenIdsRef.current.has(m.id)) continue;
        seenIdsRef.current.add(m.id);
        fresh.push(normalizeMovie(m));
      }
      if (fresh.length > 0) {
        setMovies((prev) => [...prev, ...fresh]);
      }
    };

    const run = async () => {
      // First page loads fast so the gallery has something to show quickly.
      const firstPage = await fetchPage("popular", 1);
      if (cancelledRef.current) return;
      mergeNewMovies(firstPage);
      setIsLoading(false);

      // Burst-fetch enough additional pages (in parallel batches) to clear
      // the dome's tile count before slowing down. This is what prevents
      // visible repeats even if the user spins immediately on load.
      const burstRequests = [];
      for (let page = 1; page <= BURST_PAGES_PER_LIST; page++) {
        burstRequests.push(fetchPage("top_rated", page));
        if (page > 1) burstRequests.push(fetchPage("popular", page));
      }
      const burstResults = await Promise.all(burstRequests);
      if (cancelledRef.current) return;
      for (const results of burstResults) mergeNewMovies(results);

      // Then keep growing the pool slowly in the background, alternating
      // lists so both popular and top_rated movies get represented, for
      // continued variety during longer sessions.
      const lists = ["top_rated", "popular"];
      for (
        let page = BURST_PAGES_PER_LIST + 1;
        page <= BURST_PAGES_PER_LIST + MAX_PAGES_PER_LIST;
        page++
      ) {
        for (const type of lists) {
          await new Promise((resolve) =>
            setTimeout(resolve, FETCH_INTERVAL_MS),
          );
          if (cancelledRef.current) return;
          const results = await fetchPage(type, page);
          if (cancelledRef.current) return;
          mergeNewMovies(results);
        }
      }
    };

    run();

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return { movies, isLoading };
}
